"use client";
import React, { useEffect, useState, useCallback } from "react";
import Script from "next/script";
import Breadcrumbs from "@/components/Breadcrumbs";
import ShareIcons from "@/components/Article/ShareIcons";
import RecentBlogs from "@/components/Article/RecentBlogs";
import Categories from "@/components/Article/Categories";
import BlogEnrollForm from "@/components/Article/BlogEnrollForm";
import ArticleImageBlock from "@/components/Article/ArticleImageBlock";
import RelatedBlogs from "@/components/Article/RelatedBlogs";
import ArticleBlockRenderer from "@/components/Article/ArticleBlockRenderer";
import BlogFaq from "@/components/Blogs/BlogFaq";
import { notFound } from "next/navigation";
import TableOfContents from "@/components/Article/TableOfContents";
import RelevantCourses from "@/components/Article/Course";
import {
  fetchArticleBySlug,
  fetchAllArticles,
  fetchArticlesBySubcategory,
  fetchSubcategories,
  fetchRecentBlogsByCategory,
  fetchRelatedArticles,
  // updateArticleViewCount,
} from "@/helpers/GetArticle";

export default function BlogClient({
  slug,
  initialArticle,
  initialAllArticles,
  articleUrl: serverArticleUrl,
}) {
  const [article, setArticle] = useState(initialArticle);
  const [articles, setArticles] = useState(initialAllArticles || []);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(!initialArticle);
  const [subcategoriesData, setSubcategoriesData] = useState([]);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [error, setError] = useState(null);
  const [catslug, Catslug] = useState(initialArticle?.category?.slug || [0]);
  const [catid, Setcatid] = useState(initialArticle?.category?.id || [0]);
  const [articleid, Setarticleid] = useState(initialArticle?.id || [0]);
  const [course , SetCourse ] = useState(initialArticle.demo_courses || [0]);
  const [currentsubcategories , Setcurrentsubcategories]  = useState(initialArticle.subcategories || [0]);
  const fetchData = useCallback(async () => {

    // If we already have the initial article and it matches the slug, we might not need to fetch again
    // unless there's a reason to (e.g. view count update).
    // However, the original code always fetched everything in fetchData.

    let currentArticle = initialArticle;
    let categoryId = initialArticle?.category?.id;
    let articleId = initialArticle?.id;
    let allArticlesData = initialAllArticles || [];

    try {
      if (!currentArticle) {
        setLoading(true);
        setError(null);

        const [fetchedArticleResult, allArticlesResult] = await Promise.all([
          fetchArticleBySlug(slug),
          fetchAllArticles(),
        ]);


        if (
          fetchedArticleResult.status === "fulfilled" &&
          fetchedArticleResult.value
        ) {
          currentArticle = fetchedArticleResult.value;
          setArticle(currentArticle);
          categoryId = currentArticle?.category?.id;
          articleId = currentArticle?.id;
          Setcatid(categoryId);
          Setarticleid(articleId);
        } else {
          setError("Article not found");
        }

        if (allArticlesResult.status === "fulfilled") {
          allArticlesData = allArticlesResult.value;
          setArticles(allArticlesData);
          setRelatedBlogs(allArticlesData);
        }
      } else {
        // If we have initial data, we still need to fetch subcategories and update view count
        categoryId = currentArticle?.category?.id;
      }

      //View Count Update
      // if (
      //   currentArticle?.documentId &&
      //   typeof currentArticle?.view_count === "number"
      // ) {
      //   updateArticleViewCount(
      //     currentArticle.documentId,
      //     currentArticle.view_count,
      //   ).catch((e) =>
      //     console.error("View count update failed (non-blocking):", e),
      //   );
      // }

      const currentArticleDocumentId =
        currentArticle?.documentId || currentArticle?.attributes?.documentId || null;
        const currentsubcategoriesid = currentsubcategories?.documentId
      if ( currentArticleDocumentId) {
        try {
          const relatedResult = await fetchRelatedArticles(
            currentsubcategoriesid,
            currentArticleDocumentId,
          );
          setRelatedBlogs(relatedResult && relatedResult.length > 0 ? relatedResult : allArticlesData);
        } catch (relatedErr) {
          console.error("Related articles fetch failed:", relatedErr);
          setRelatedBlogs(allArticlesData);
        }
      } else {
        setRelatedBlogs(allArticlesData);
      }
      // Now, fetch subcategories using the categoryId if it exists
      if (categoryId) {
        const subcategoriesResult = await fetchSubcategories(categoryId);
        const recentBlogsResult = await fetchRecentBlogsByCategory(categoryId);


        if (subcategoriesResult && subcategoriesResult.length > 0) {
          setSubcategoriesData(subcategoriesResult);
        } else {
          setSubcategoriesData([]);
        }

        if (recentBlogsResult && recentBlogsResult.length > 0) {
          setRecentBlogs(recentBlogsResult);
        } else {
          setRecentBlogs([]);
        }
      } else {
        setSubcategoriesData([]);
        setRecentBlogs([]);
      }
    } catch (err) {
      console.error("Unexpected error in fetchData:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [slug, initialArticle]);

  // Fetch data when the component mounts or slug changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Loading state
  if (loading && !article) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !article) {
    notFound();
  }


  // Use the server-provided stable URL when available to avoid
  // hydration mismatches between SSR and client render.
  const articleUrl =
    serverArticleUrl ||
    (typeof window !== "undefined" ? window.location.href : "");
  const articleTitle =
    article?.attributes?.title || article?.title || "Article";
  const articleAltTitle =
    article?.cover?.alternativeText || article?.title || "Article";

  /* --------------------
     EXTRACT HEADINGS FOR TOC
     -------------------- */
  const extractHeadingsFromArticle = (articleObj) => {
    const headings = [];

    if (!articleObj) return headings;

    const blocks =
      articleObj.blocks && articleObj.blocks.length > 0
        ? articleObj.blocks
        : articleObj.attributes?.blocks || [];

    const cleanMarkdownText = (s) =>
      (s || "")
        .toString()
        .replace(/\*\*|__|\*|_|`/g, "")
        .replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1")
        .trim();

    const slugify = (text) =>
      cleanMarkdownText(text)
        .toString()
        .toLowerCase()
        .trim()
        .replace(/<[^>]+>/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/[\s]+/g, "-");

    blocks.forEach((block) => {
      if (!block) return;

      // Support both Strapi rich-text and editor blocks.
      // Try common fields in order: body, content, value, editor.html
      const isRichText = block.__component === "shared.rich-text";
      const isEditor = block.__component === "shared.editor";
      if (!isRichText && !isEditor) return;

      const raw =
        (block.body &&
          (typeof block.body === "string"
            ? block.body
            : block.body.toString && block.body.toString())) ||
        (block.content &&
          (typeof block.content === "string"
            ? block.content
            : block.content.toString && block.content.toString())) ||
        (block.value &&
          (typeof block.value === "string"
            ? block.value
            : block.value.toString && block.value.toString())) ||
        // Support editor content as a plain string or as an object with an `html` property.
        (typeof block.editor === "string"
          ? block.editor
          : block.editor && block.editor.html) ||
        // Some payloads use capitalized `Editor` key holding markdown/html
        (typeof block.Editor === "string"
          ? block.Editor
          : block.Editor && block.Editor.html) ||
        "";

      // If the content looks like HTML, extract headings from <hN> tags
      if (/<h[1-6]/i.test(raw)) {
        const hRegex = /<h([1-6])[^>]*>(.*?)<\/h\1>/gi;
        let match;
        while ((match = hRegex.exec(raw)) !== null) {
          const level = parseInt(match[1], 10);
          const rawText = match[2].replace(/<[^>]+>/g, "").trim();
          const displayText = cleanMarkdownText(rawText);
          if (level === 2) {
            const id = slugify(displayText);
            headings.push({ level, text: displayText, id });
          }
        }
      } else {
        // Fallback: treat as Markdown and extract # headings
        const mdRegex = /^(#{1,6})\s+(.+)$/gm;
        let match;
        while ((match = mdRegex.exec(raw)) !== null) {
          const level = match[1].length;
          const rawText = match[2].trim();
          const displayText = cleanMarkdownText(rawText);
          if (level === 2) {
            const id = slugify(displayText);
            headings.push({ level, text: displayText, id });
          }
        }
      }
    });

    return headings;
  };

  const hasFaqs = Array.isArray(article?.BlogFaq) && article.BlogFaq.length > 0;
  const tocHeadings = [
    ...extractHeadingsFromArticle(article),
    ...(hasFaqs ? [{ level: 2, text: "Frequently Asked Questions", id: "faqs" }] : []),
  ];

  const extractYouTubeVideoIdFromArticle = (articleObj) => {
    if (!articleObj) return null;

    const blocks =
      articleObj.blocks && articleObj.blocks.length > 0
        ? articleObj.blocks
        : articleObj.attributes?.blocks || [];

    for (const block of blocks) {
      if (!block) continue;

      const isRichText = block.__component === "shared.rich-text";
      const isEditor = block.__component === "shared.editor";
      if (!isRichText && !isEditor) continue;

      const raw =
        (block.body &&
          (typeof block.body === "string"
            ? block.body
            : block.body.toString && block.body.toString())) ||
        (block.content &&
          (typeof block.content === "string"
            ? block.content
            : block.content.toString && block.content.toString())) ||
        (block.value &&
          (typeof block.value === "string"
            ? block.value
            : block.value.toString && block.value.toString())) ||
        (typeof block.editor === "string"
          ? block.editor
          : block.editor && block.editor.html) ||
        (typeof block.Editor === "string"
          ? block.Editor
          : block.Editor && block.Editor.html) ||
        "";

      // Look for an iframe src attribute
      const iframeMatch = raw.match(/<iframe[^>]*src=["']([^"']+)["'][^>]*>/i);
      if (iframeMatch && iframeMatch[1]) {
        const src = iframeMatch[1];
        const idMatch =
          src.match(/youtube\.com\/embed\/([A-Za-z0-9_-]+)/i) ||
          src.match(/youtube\.com\/watch\?v=([A-Za-z0-9_-]+)/i) ||
          src.match(/youtu\.be\/([A-Za-z0-9_-]+)/i);
        if (idMatch && idMatch[1]) return idMatch[1];
      }
    }

    return null;
  };

  const videoId = extractYouTubeVideoIdFromArticle(article);
  const videoSchema = videoId
    ? {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: articleTitle,
        description:
          article?.attributes?.meta_description ||
          article?.description ||
          articleAltTitle,
        thumbnailUrl: [`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`],
        uploadDate:
          article?.attributes?.publishedAt ||
          article?.publishedAt ||
          article?.createdAt ||
          undefined,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
        contentUrl: `https://www.youtube.com/watch?v=${videoId}`,
      }
    : null;

  const extractPlainTextFromArticle = (articleObj) => {
    if (!articleObj) return "";

    const blocks =
      articleObj.blocks && articleObj.blocks.length > 0
        ? articleObj.blocks
        : articleObj.attributes?.blocks || [];

    const getRawFromBlock = (block) => {
      if (!block) return "";
      return (
        (block.body &&
          (typeof block.body === "string"
            ? block.body
            : block.body.toString && block.body.toString())) ||
        (block.content &&
          (typeof block.content === "string"
            ? block.content
            : block.content.toString && block.content.toString())) ||
        (block.value &&
          (typeof block.value === "string"
            ? block.value
            : block.value.toString && block.value.toString())) ||
        (typeof block.editor === "string"
          ? block.editor
          : block.editor && block.editor.html) ||
        (typeof block.Editor === "string"
          ? block.Editor
          : block.Editor && block.Editor.html) ||
        ""
      );
    };

    const parts = [];
    blocks.forEach((block) => {
      const raw = getRawFromBlock(block) || "";
      if (!raw) return;

      // Remove HTML tags, convert markdown links to plain text, strip extra markup
      let text = raw
        .toString()
        .replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1")
        .replace(/<[^>]+>/g, " ")
        .replace(/[\*`_>#\-]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      if (text) parts.push(text);
    });

    return parts.join(" \n\n ").trim();
  };

  const articlePlainBody = extractPlainTextFromArticle(article);

  const getCategoryName = (art) => {
    return (
      art?.category?.name ||
      art?.attributes?.category?.name ||
      art?.category?.data?.attributes?.name ||
      art?.attributes?.category?.data?.attributes?.name ||
      null
    );
  };

  const getAuthorName = (art) => {
    const a =
      art?.author ||
      art?.attributes?.author ||
      art?.author?.data ||
      art?.attributes?.author?.data;
    return a?.name || a?.attributes?.name || a?.data?.attributes?.name || null;
  };

  const siteBase =
    process.env.NEXT_PUBLIC_CLIENT_URL || "https://www.msu.edu.in";
  const articleImage =
    article?.cover?.url ||
    article?.cover?.formats?.large?.url ||
    article?.attributes?.cover?.url ||
    article?.attributes?.cover?.formats?.large?.url ||
    null;

  // Blog image schema
  const articleCoverObj =
  article?.cover || article?.attributes?.cover || null;

const articleImageWidth =
  articleCoverObj?.width ||
  articleCoverObj?.formats?.large?.width ||
  undefined;

const articleImageHeight =
  articleCoverObj?.height ||
  articleCoverObj?.formats?.large?.height ||
  undefined;

const imageSchema = articleImage
  ? {
      "@context": "https://schema.org",
      "@type": "ImageObject",
      contentUrl: articleUrl,
      url: articleImage,
      thumbnailUrl: articleImage,
      width: articleImageWidth,
      height: articleImageHeight,
      caption: articleAltTitle,
       description:
      article?.SEO?.[0]?.metaDescription ||
      article?.description ||
      articleAltTitle,
      representativeOfPage: true,
      author: {
        "@type": "Organization",
        name: "Medhavi Skills University",
        url:  "https://www.msu.edu.in"
      },
    }
  : null; 

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
    headline: articleTitle,
    image: articleImage ? [articleImage] : [],
    datePublished:
      article?.attributes?.publishedAt ||
      article?.publishedAt ||
      article?.createdAt ||
      undefined,
    dateModified:
      article?.attributes?.updatedAt || article?.updatedAt || undefined,
    author: [
      {
        "@type": "Person",
        name: getAuthorName(article) || undefined,
        url:
          (article?.author?.slug &&
            `${siteBase}/author/${article.author.slug}`) ||
          (article?.attributes?.author?.slug &&
            `${siteBase}/author/${article.attributes.author.slug}`) ||
          undefined,
      },
    ],
    publisher: {
      "@type": "Organization",
      name: "Medhavi Skills University",
      url: siteBase,
      logo: {
        "@type": "ImageObject",
        url: `${siteBase}/logo.svg`,
      },
    },
    articleSection: getCategoryName(article) ? [getCategoryName(article)] : [],
    inLanguage: "en-IN",
    isFamilyFriendly: "true",
    description:
      article?.SEO?.[0]?.metaDescription ||
      article?.description ||
      articleAltTitle,
    articleBody: articlePlainBody,
  };

  return (
    <main className="bg-white w-full mx-auto relative blog-page">
      <Script
        src="https://www.google.com/recaptcha/api.js?render=explicit"
        strategy="afterInteractive"
      />
      {articleSchema && (
        <Script
          id="article-schema"
          type="application/ld+json"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
      )}
      {imageSchema && (
        <Script
          id="image-schema"
          type="application/ld+json"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(imageSchema) }}
        />
      )}
      <article className="w-full">
        <ArticleImageBlock
          article={article}
          articleUrl={articleUrl}
          articleTitle={articleTitle}
          articleAltTitle={articleAltTitle}
        />
        <Breadcrumbs
          items={[
            { label: "Blogs", href: "/blog" },
            { label: article?.subcategories?.name, href: `/blog/${article?.category?.slug}/${article?.subcategories?.slug || ""}` },
            { label: article?.title || article?.attributes?.title },
          ]}
        />
        <div className="px-[var(--container-padding)] max-w-[var(--max-page-width)] mx-auto mt-[47px] lg:mt-[60px] lg:flex gap-[30px] md:gap-[40px] lg:gap-[60px]">
          <div className="lg:w-[65%]">
            <div
              className="tracking-normal align-middle mb-[27px]"
              style={{ fontFamily: "saans-regular" }}
            >
              <div
                className="tracking-normal align-middle mb-[27px]"
                style={{ fontFamily: "saans-regular" }}
              >
                {tocHeadings && tocHeadings.length > 0 && (
                  <div className="block">
                    <TableOfContents headings={tocHeadings} />
                  </div>
                )}
                {article.blocks && article.blocks.length > 0 ? (
                  article.blocks.map((block, index) => (
                    <ArticleBlockRenderer key={index} block={block} />
                  ))
                ) : article.attributes?.blocks &&
                  article.attributes.blocks.length > 0 ? (
                  article.attributes.blocks.map((block, index) => (
                    <ArticleBlockRenderer key={index} block={block} />
                  ))
                ) : (
                  <p>This article has no content yet.</p>
                )}
              </div>
            </div>
          </div>

          <div className="lg:w-[35%] gap-[12px] flex flex-col">
            <div className="">
              <RecentBlogs blogs={recentBlogs} article={article} />
            </div>
            <Categories catslug={catslug} subcat={subcategoriesData} />
            <RelevantCourses courses={course} />
             {/* sticky wrapper */}
            <div className="lg:sticky lg:top-[120px] lg:self-start lg:w-full">
              <BlogEnrollForm catId={catid} slug={slug} />
            </div>
          </div>
        </div>
        <BlogFaq data={article?.BlogFaq} />
        <RelatedBlogs blogs={relatedBlogs} article={article} />
      </article>

      <ShareIcons
        alignment={
          "flex-col absolute top-[50%] right-[30px] gap-[20px] hidden lg:flex"
        }
        articleUrl={articleUrl}
        articleTitle={articleTitle}
      />
    </main>
  );
}
